import { motion } from 'framer-motion'
import type { StageConfigData } from '@/components/Configuracao/fluxosPipelineFlow'

const PIPELINE_STEPS = [
  {
    title: 'Qualificação',
    stages: ['QUALIFICADO'],
    desc: 'Lead qualificado pelo BDR ou prospecção ativa. Deal criado vinculado a uma Empresa. Probabilidade: 20%.',
  },
  {
    title: 'Reuniões',
    stages: ['REUNIAO_AGENDADA', 'REUNIAO_REALIZADA'],
    desc: 'Closer agenda a reunião e a realiza. SLA e drift de dias passam a contar. Ao concluir, preenche qualificação pós-reunião (Budget, Decisor, Dores).',
  },
  {
    title: 'Proposta Comercial',
    stages: ['PROPOSTA_ELABORACAO', 'PROPOSTA_ENVIADA'],
    desc: 'Closer elabora a proposta no CRM e envia ao cliente. Pode ir para 30 dias Grátis, Renegociação, Gerar Contrato ou Perda.',
  },
  {
    title: 'Contrato',
    stages: ['GERAR_CONTRATO', 'CONTRATO_ENVIADO'],
    desc: 'Geração do contrato e envio para assinatura. Após enviado, pode ser marcado como Ganho (gera onboarding, financeiro, comissões).',
  },
] as const

const SPECIAL_TRANSITIONS = [
  { icon: '✗', label: 'Perda', desc: 'Disponível em quase todos os estágios. Requer motivo, submotivo e comentário.' },
  { icon: '⏸', label: 'Pausar', desc: 'Suspende temporariamente o deal. Pode reativar para qualquer estágio posteriormente.' },
  { icon: '📅', label: 'Fechamento Futuro', desc: 'Cliente pediu prazo maior. Requer data de fechamento prevista.' },
  { icon: '🎁', label: '30 dias Grátis', desc: 'Cliente testa por 30 dias. O CRM cria um lembrete para retomar a negociação.' },
  { icon: '✓', label: 'Ganho', desc: 'Somente após Contrato Enviado. Exige observação detalhando o projeto vendido e gera onboarding automático.' },
] as const

export function FluxosDealsStepsPanel({ stageConfig }: { stageConfig: StageConfigData }) {
  return (
    <motion.div
      key="dynamic-deals"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className="space-y-4"
    >
      <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 space-y-2">
        <h4 className="text-xs font-bold text-text-muted uppercase tracking-wider flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-accent" />
          Visão Geral
        </h4>
        <p className="text-xs text-text-muted leading-relaxed">
          Pipeline de 12 estágios divididos em 3 grupos: <strong className="text-text-primary">Ativos</strong>{' '}
          (em negociação), <strong className="text-emerald-400"> Fechados</strong> (ganho/perdido) e{' '}
          <strong className="text-zinc-400"> Inativos</strong> (pausado/futuro). Cada seta representa uma
          transição válida entre estágios.
        </p>
      </div>

      <div className="space-y-2">
        <h4 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-accent" />
          Pipeline Principal
        </h4>
        {PIPELINE_STEPS.map((step, i) => (
          <div key={step.title} className="flex gap-3 group">
            <div className="shrink-0 flex flex-col items-center">
              <div className="w-6 h-6 rounded-full bg-accent/20 border border-accent/30 text-accent flex items-center justify-center text-xs font-bold">
                {i + 1}
              </div>
              <div className="w-px h-full bg-white/5 my-1 group-last:hidden" />
            </div>
            <div className="pb-3">
              <h4 className="text-sm font-bold text-text-primary mb-1">{step.title}</h4>
              <div className="flex flex-wrap gap-1 mb-1.5">
                {step.stages.map((sId) => {
                  const s = stageConfig.stages.find((st) => st.id === sId)
                  if (!s) return null
                  return (
                    <span
                      key={s.id}
                      className="text-[10px] px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: `${s.color}20`, color: s.color }}
                    >
                      {s.label}
                    </span>
                  )
                })}
              </div>
              <p className="text-xs text-text-muted leading-relaxed">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <h4 className="text-xs font-bold text-text-muted uppercase tracking-wider flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-zinc-500" />
          Transições Especiais
        </h4>
        {SPECIAL_TRANSITIONS.map((item) => (
          <div
            key={item.label}
            className="flex items-center gap-2.5 p-2 rounded-lg bg-white/[0.02] border border-white/5"
          >
            <span className="text-xs shrink-0">{item.icon}</span>
            <div className="flex flex-col min-w-0">
              <span className="text-xs text-text-primary truncate">{item.label}</span>
              <span className="text-[10px] text-text-muted">{item.desc}</span>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  )
}
